"use client";

import type { CSSProperties, ReactNode, Ref } from "react";
import { Loader2, X } from "lucide-react";
import { AeraButton } from "./AeraButton";

export type ModalCloseButtonProps = {
  onClose: () => void;
  className?: string;
  disabled?: boolean;
  "aria-label"?: string;
};

export function ModalCloseButton({
  onClose,
  className = "",
  disabled = false,
  "aria-label": ariaLabel = "ปิด",
}: ModalCloseButtonProps) {
  return (
    <AeraButton
      variant="transparent"
      size="xs"
      type="button"
      onClick={onClose}
      disabled={disabled}
      aria-label={ariaLabel}
      className={[
        // "rounded-full p-2! text-aera-gray transition-colors hover:bg-aera-warmcream/50 hover:text-aera-charcoal disabled:cursor-not-allowed disabled:opacity-50 h-auto! w-auto! min-w-0!",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <X className="h-5 w-5" strokeWidth={2} aria-hidden />
    </AeraButton>
  );
}

const MODAL_HEADER_SHELL_CLASSNAME =
  "shrink-0 px-4 py-4 sm:px-6";

/** สไตล์ header modal มาตรฐาน — แก้ที่เดียว */
export const MODAL_HEADER_CLASSNAME = [
  MODAL_HEADER_SHELL_CLASSNAME,
  "bg-aera-offwhite",
].join(" ");

export type ModalHeaderProps = {
  title?: ReactNode;
  onClose?: () => void;
  hideCloseButton?: boolean;
  closeDisabled?: boolean;
  /** แทนที่แถว title ทั้งแถว (เช่น header สีตามสถานะ) */
  children?: ReactNode;
  className?: string;
  /** false = ไม่ใส่ gradient มาตรฐาน (ใช้ className กำหนดพื้นหลังเอง) */
  surface?: boolean;
};

function modalHeaderClassName(surface: boolean, className: string) {
  return [
    surface ? MODAL_HEADER_CLASSNAME : MODAL_HEADER_SHELL_CLASSNAME,
    className,
  ]
    .filter(Boolean)
    .join(" ");
}

export function ModalHeader({
  title,
  onClose,
  hideCloseButton = false,
  closeDisabled = false,
  children,
  className = "",
  surface = true,
}: ModalHeaderProps) {
  if (children) {
    return (
      <div className={modalHeaderClassName(surface, className)}>{children}</div>
    );
  }

  return (
    <div
      className={[
        modalHeaderClassName(surface, className),
        "flex items-center justify-between gap-3",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="min-w-0 flex-1 text-lg font-bold text-aera-charcoal">
        {typeof title === "string" ? (
          <span className="block truncate">{title}</span>
        ) : (
          title
        )}
      </div>
      {!hideCloseButton && onClose ? (
        <ModalCloseButton
          onClose={onClose}
          disabled={closeDisabled}
          className="-mr-1"
        />
      ) : null}
    </div>
  );
}

export type ModalBodyProps = {
  children: ReactNode;
  className?: string;
  ref?: Ref<HTMLDivElement>;
};

export function ModalBody({ children, className = "", ref }: ModalBodyProps) {
  return (
    <div
      ref={ref}
      className={[
        "relative min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
}

export type ModalBodyContentProps = {
  loading?: boolean;
  /** แสดงใน body ขณะ loading (เช่น skeleton) */
  loadingContent?: ReactNode;
  children: ReactNode;
  minHeightClassName?: string;
  style?: CSSProperties;
  className?: string;
  ref?: Ref<HTMLDivElement>;
};

export function ModalBodyContent({
  loading = false,
  loadingContent,
  children,
  minHeightClassName = "min-h-auto",
  style,
  className = "",
  ref,
}: ModalBodyContentProps) {
  return (
    <ModalBody ref={ref} className={className}>
      <div className={["relative w-full min-w-0", minHeightClassName].join(" ")} style={style}>
        {loading ? (
          loadingContent ?? (
            <div className="flex h-full min-h-[inherit] flex-col items-center justify-center gap-2 py-10 text-aera-gray">
              <Loader2
                className="h-7 w-7 animate-spin text-aera-amber"
                aria-hidden
              />
              <p className="text-sm font-medium text-aera-charcoal">
                กำลังโหลด...
              </p>
            </div>
          )
        ) : (
          children
        )}
      </div>
    </ModalBody>
  );
}

const MODAL_FOOTER_SHADOW_CLASS =
  "shadow-[0_2px_10px_0px] shadow-aera-gray/20";

export type ModalFooterProps = {
  children: ReactNode;
  className?: string;
  /** แสดงเงาเมื่อ body scroll ได้ */
  elevated?: boolean;
};

export function ModalFooter({
  children,
  className = "",
  elevated = false,
}: ModalFooterProps) {
  return (
    <div
      className={[
        "relative z-1 shrink-0 bg-aera-offwhite px-4 py-4 sm:px-6 transition-shadow duration-200",
        elevated && MODAL_FOOTER_SHADOW_CLASS,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
        {children}
      </div>
    </div>
  );
}
